import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { folders, files, activities } from '../api';
import FileExplorer from '../components/FileExplorer';
import ActivityPanel from '../components/ActivityPanel';
import '../styles/Dashboard.scss';

const DashboardPage = ({ currentUser, onLogout }) => {
  const [foldersList, setFoldersList] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderContents, setFolderContents] = useState({ folder: null, files: [] });
  const [loading, setLoading] = useState(true);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const response = await folders.list();
      setFoldersList(response.data.folders || []);

      if (response.data.folders?.length > 0) {
        setCurrentFolder(response.data.folders[0].id);
        await fetchFolderContents(response.data.folders[0].id);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderContents = async (folderId) => {
    try {
      const response = await folders.get(folderId);
      setFolderContents({
        folder: response.data.folder,
        files: response.data.files || [],
      });
      setCurrentFolder(folderId);
    } catch (error) {
      console.error('Error fetching folder contents:', error);
    }
  };

  const handleFolderSelect = (folderId) => {
    fetchFolderContents(folderId);
  };

  const handleCreateFolder = async (parentId, folderName) => {
    try {
      await folders.create(parentId, folderName);
      await fetchFolders();
      await fetchFolderContents(parentId);
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleUploadFile = async (folderId, file) => {
    try {
      await files.upload(folderId, file);
      await fetchFolderContents(folderId);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await files.delete(fileId);
      if (currentFolder) {
        await fetchFolderContents(currentFolder);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleShowActivities = async () => {
    try {
      const response = await activities.list();
      setActivityList(response.data.activities || []);
      setShowActivityPanel(true);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Cloud File Storage</h1>
        </div>
        <div className="header-right">
          <span className="user-info">{currentUser?.username}</span>
          <button className="btn-secondary" onClick={handleShowActivities}>
            Activity
          </button>
          <button className="btn-danger" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-container">
        <div className="sidebar">
          <h3>Folders</h3>
          <div className="folders-list">
            {foldersList.length > 0 ? (
              foldersList.map((folder) => (
                <div
                  key={folder.id}
                  className={`folder-item ${currentFolder === folder.id ? 'active' : ''}`}
                  onClick={() => handleFolderSelect(folder.id)}
                >
                  📁 {folder.name}
                </div>
              ))
            ) : (
              <p className="no-folders">No folders available</p>
            )}
          </div>
        </div>

        <div className="main-content">
          {currentFolder && (
            <FileExplorer
              folder={folderContents.folder}
              files={folderContents.files}
              onCreateFolder={handleCreateFolder}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
            />
          )}
        </div>
      </div>

      {showActivityPanel && (
        <ActivityPanel
          activities={activityList}
          onClose={() => setShowActivityPanel(false)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
